import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  BadgeEuro,
  ChevronRight,
  FileDown,
  Printer,
  Receipt,
  Send,
  ShieldCheck,
  ShieldQuestion,
} from 'lucide-react';
import type { TenantSummary } from '@forq/contracts';
import type { OrderGateway, OrderRecord } from '@forq/orders-data';
import { CustomizableMetrics, EmptyState, MetricTile, WorkspacePage } from '@forq/ui';
import { buildBookingRows, mayExport, validateForExport } from '@forq/finance-data';
import { downloadDatevCsv, toDatevCsv } from '@forq/finance-data';
import { FinanceNav } from './FinanceNav';
import { computeFinanceKpis, incomingInPeriod, invoicesInPeriod, openAmount, openReceivables } from '@forq/finance-data';
import { dateLabel, errorMessage, money } from '@forq/finance-data';
import { PeriodFilter, useFinancePeriod } from './PeriodFilter';
import { downloadOposCsv } from '@forq/finance-data';
import { daysOverdue, isInvoiceOverdue, todayIso, type FinanceGateway, type Invoice } from '@forq/finance-data';
import { type IncomingInvoice, type IncomingInvoiceGateway } from '@forq/finance-data';

interface CockpitPageProps {
  readonly tenant: TenantSummary;
  readonly mode: 'demo' | 'supabase';
  readonly permissions: ReadonlySet<string>;
  readonly financeGateway: FinanceGateway;
  readonly incomingGateway: IncomingInvoiceGateway;
  readonly ordersGateway: OrderGateway;
}

/** The finance process, end to end — each step navigates to the area that owns it. */
const processChain: readonly { readonly key: string; readonly label: string; readonly route: string }[] = [
  { key: 'ready', label: 'Bereit zur Abrechnung', route: '/finance/abrechnung' },
  { key: 'create', label: 'Rechnung erstellen', route: '/finance/abrechnung' },
  { key: 'approve', label: 'Freigeben', route: '/finance/abrechnung' },
  { key: 'send', label: 'Versenden', route: '/finance/abrechnung' },
  { key: 'opos', label: 'OPOS & Zahlung', route: '/finance/offene-posten' },
  { key: 'report', label: 'Auswertung / Export', route: '/finance/auswertungen' },
];

type WorkTone = 'default' | 'warning' | 'danger';

interface WorkItem {
  readonly key: string;
  readonly number: string;
  readonly party: string;
  readonly hint: string;
  readonly amount: number;
  readonly cta: string;
  readonly route: string;
  readonly tone: WorkTone;
  /** Sort weight — the most urgent work first. */
  readonly urgency: number;
}

/**
 * The Arbeitsvorrat: every finance task that is waiting on someone, in ONE list, derived from the
 * same records the KPIs use. This is the cockpit's actual promise — "was muss als Nächstes
 * passieren?" without hunting through four areas.
 */
function buildWorkItems(
  orders: readonly OrderRecord[],
  invoices: readonly Invoice[],
  incoming: readonly IncomingInvoice[],
  today: string,
): readonly WorkItem[] {
  const items: WorkItem[] = [];

  for (const order of orders) {
    items.push({
      key: `order-${order.id}`,
      number: order.number,
      party: order.customerName,
      hint: 'Vorgang bereit zur Abrechnung',
      amount: order.grossTotal,
      cta: 'Rechnung',
      route: '/finance/abrechnung',
      tone: 'default',
      urgency: 4,
    });
  }

  for (const invoice of invoices) {
    const target = `/finance/rechnungen/${invoice.invoiceNumber ?? invoice.proposalNumber}`;
    if (invoice.documentStatus === 'in_review') {
      items.push({
        key: `inv-${invoice.id}`, number: invoice.proposalNumber, party: invoice.customerName,
        hint: 'Rechnung wartet auf Freigabe', amount: invoice.grossTotal,
        cta: 'Freigeben', route: target, tone: 'warning', urgency: 2,
      });
    } else if (invoice.documentStatus === 'approved') {
      items.push({
        key: `inv-${invoice.id}`, number: invoice.proposalNumber, party: invoice.customerName,
        hint: 'Freigegeben – Rechnung erstellen', amount: invoice.grossTotal,
        cta: 'Erstellen', route: target, tone: 'default', urgency: 3,
      });
    } else if (invoice.documentStatus === 'invoiced') {
      items.push({
        key: `inv-${invoice.id}`, number: invoice.invoiceNumber ?? invoice.proposalNumber, party: invoice.customerName,
        hint: 'Rechnung erstellt – noch nicht versendet', amount: invoice.grossTotal,
        cta: 'Versenden', route: target, tone: 'default', urgency: 3,
      });
    }
    if (isInvoiceOverdue(invoice, today)) {
      const days = daysOverdue(invoice, today);
      items.push({
        key: `dun-${invoice.id}`, number: invoice.invoiceNumber ?? invoice.proposalNumber, party: invoice.customerName,
        hint: `Überfällig seit ${days} ${days === 1 ? 'Tag' : 'Tagen'} · fällig ${dateLabel(invoice.dueDate)}`,
        amount: openAmount(invoice), cta: 'Mahnen', route: '/finance/mahnwesen', tone: 'danger', urgency: 1,
      });
    }
  }

  for (const invoice of incoming) {
    if (invoice.status === 'review_requested') {
      items.push({
        key: `er-${invoice.id}`, number: invoice.number, party: invoice.supplierName,
        hint: 'Eingangsrechnung wartet auf Fachfreigabe', amount: invoice.grossAmount,
        cta: 'Prüfen', route: '/finance/eingangsrechnungen', tone: 'warning', urgency: 2,
      });
    } else if (invoice.status === 'payment_requested') {
      items.push({
        key: `er-${invoice.id}`, number: invoice.number, party: invoice.supplierName,
        hint: 'Eingangsrechnung wartet auf Zahlungsfreigabe', amount: invoice.grossAmount,
        cta: 'Freigeben', route: '/finance/eingangsrechnungen', tone: 'warning', urgency: 2,
      });
    }
  }

  return items.sort((left, right) => left.urgency - right.urgency || right.amount - left.amount);
}

const workIcons: Readonly<Record<WorkTone, typeof Receipt>> = {
  default: Receipt,
  warning: ShieldQuestion,
  danger: AlertTriangle,
};

export function CockpitPage({ tenant, mode, permissions, financeGateway, incomingGateway, ordersGateway }: CockpitPageProps) {
  const navigate = useNavigate();
  const [period, setPeriod] = useFinancePeriod();
  const [invoices, setInvoices] = useState<readonly Invoice[]>([]);
  const [incoming, setIncoming] = useState<readonly IncomingInvoice[]>([]);
  const [billingReady, setBillingReady] = useState<readonly OrderRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const today = todayIso();
  const canExport = permissions.has('finance.view');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [invoiceList, incomingList, orderPage] = await Promise.all([
        financeGateway.listInvoices({ tenantId: tenant.id }),
        incomingGateway.listIncomingInvoices({ tenantId: tenant.id }),
        ordersGateway.searchOrders({ tenantId: tenant.id, bucket: 'all', pageSize: 50 }),
      ]);
      setInvoices(invoiceList);
      setIncoming(incomingList);
      setBillingReady(orderPage.items.filter((order) => order.status === 'billing-ready'));
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [financeGateway, incomingGateway, ordersGateway, tenant.id]);

  useEffect(() => { void load(); }, [load]);

  const kpis = useMemo(() => computeFinanceKpis(invoices, incoming, period), [invoices, incoming, period]);
  const readySum = useMemo(() => billingReady.reduce((sum, order) => sum + order.grossTotal, 0), [billingReady]);
  const workItems = useMemo(
    () => buildWorkItems(billingReady, invoices, incoming, today),
    [billingReady, invoices, incoming, today],
  );
  const overdueCount = useMemo(
    () => openReceivables(invoices).filter((invoice) => isInvoiceOverdue(invoice, today)).length,
    [invoices, today],
  );

  /**
   * The same gate as on the Buchhaltung page — a Schnellaktion must not be a side door around the
   * export check. Blocked exports send the user to where the errors are actually listed.
   */
  const datevBlocked = useMemo(
    () => !mayExport(validateForExport(invoicesInPeriod(invoices, period), incomingInPeriod(incoming, period))),
    [invoices, incoming, period],
  );

  const exportDatev = useCallback(() => {
    if (datevBlocked) {
      navigate('/finance/buchhaltung');
      return;
    }
    const rows = buildBookingRows(invoicesInPeriod(invoices, period), incomingInPeriod(incoming, period));
    downloadDatevCsv(toDatevCsv(rows), tenant.shortName);
  }, [datevBlocked, navigate, invoices, incoming, period, tenant.shortName]);

  const exportOpos = useCallback(() => {
    downloadOposCsv(invoices, incoming, today, tenant.shortName);
  }, [invoices, incoming, today, tenant.shortName]);

  const visibleWork = workItems.slice(0, 8);

  return (
    <WorkspacePage
      customizeKey="finance-cockpit"
      eyebrow={`FORQ Finance Center · ${mode === 'demo' ? 'Demo' : 'Supabase'}`}
      title="Finanzen schnell, sauber und verständlich."
      description="Ein Arbeitscenter für Abrechnung, OPOS, Freigaben, Zahlungen, Auswertungen und vorbereitende Buchhaltung – mit einer zentralen Sicht auf Zahlen und Status."
      actions={(
        <button type="button" className="finance-actions__btn" onClick={() => window.print()}>
          <Printer size={15} /> Übersicht drucken
        </button>
      )}
      metrics={(
        <CustomizableMetrics
          pageKey="finance-cockpit"
          tiles={[
            { key: 'ready', node: <MetricTile label="Bereit zur Abrechnung" value={money(readySum)} hint={`${billingReady.length} Vorgänge`} tone="info" /> },
            { key: 'receivables', node: <MetricTile label="Offene Forderungen" value={money(kpis.receivablesOpen)} hint={`${kpis.receivablesCount} Debitoren`} tone={overdueCount > 0 ? 'danger' : 'default'} /> },
            { key: 'payables', node: <MetricTile label="Offene Verbindlichkeiten" value={money(kpis.payablesOpen)} hint={`${kpis.payablesCount} Kreditoren`} tone="warning" /> },
            { key: 'grossprofit', node: <MetricTile label="Rohertrag" value={money(kpis.grossProfit)} hint={`Umsatz ${money(kpis.revenue)} · Aufwand ${money(kpis.expense)}`} tone={kpis.grossProfit < 0 ? 'danger' : 'default'} /> },
          ]}
        />
      )}
      toolbar={<div className="finance-toolbar"><FinanceNav /><PeriodFilter period={period} onChange={setPeriod} /></div>}
    >
      {error && <div className="finance-page__error">{error}</div>}

      <p className="finance-note">
        <ShieldCheck size={15} aria-hidden />
        <span>
          <strong>Zahlenlogik zentralisiert:</strong> Rechnungen, OPOS, Auswertungen und Exporte rechnen auf derselben
          Basis. Bezahlt wird in OPOS gepflegt; die Abrechnung bleibt reine Rechnungsbearbeitung.
        </span>
      </p>

      <nav className="finance-chain" aria-label="Finance-Prozesskette">
        {processChain.map((step, index) => (
          <span key={step.key} className="finance-chain__step">
            <button type="button" onClick={() => navigate(step.route)}>{step.label}</button>
            {index < processChain.length - 1 && <ChevronRight size={14} aria-hidden />}
          </span>
        ))}
      </nav>

      <div className="finance-cockpit">
        <section className="finance-work">
          <header className="finance-work__head">
            <div>
              <span className="finance-work__eyebrow">Arbeitsvorrat</span>
              <h3>Was muss als Nächstes passieren?</h3>
              <p>Alle offenen Finance-Aufgaben in einer Liste – ohne Suchen in mehreren Bereichen.</p>
            </div>
            <button type="button" className="finance-actions__btn" onClick={() => navigate('/finance/abrechnung')}>
              Abrechnung öffnen <ArrowRight size={14} />
            </button>
          </header>

          {loading ? (
            <p className="finance-work__empty">Arbeitsvorrat wird geladen …</p>
          ) : visibleWork.length === 0 ? (
            <EmptyState eyebrow="Arbeitsvorrat" title="Nichts offen" description="Es wartet gerade keine Finance-Aufgabe auf Bearbeitung." />
          ) : (
            <ul className="finance-work__list">
              {visibleWork.map((item) => {
                const Icon = workIcons[item.tone];
                return (
                  <li key={item.key} className={`finance-work__item is-${item.tone}`}>
                    <span className="finance-work__icon"><Icon size={15} aria-hidden /></span>
                    <span className="forq-stack">
                      <strong>{item.number} · {item.party}</strong>
                      <small>{item.hint}</small>
                    </span>
                    <span className="finance-work__value forq-cell-num">{money(item.amount)}</span>
                    <button type="button" className="finance-work__cta" onClick={() => navigate(item.route)}>
                      {item.cta}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {workItems.length > visibleWork.length && (
            <p className="finance-work__more">und {workItems.length - visibleWork.length} weitere Aufgaben</p>
          )}
        </section>

        <aside className="finance-quick">
          <span className="finance-work__eyebrow">Schnellaktionen</span>
          <h3>Ausgaben & Exporte</h3>
          <button type="button" className="finance-quick__item" onClick={() => window.print()}>
            <Printer size={16} aria-hidden />
            <span className="forq-stack"><strong>Übersicht drucken</strong><small>Aktuelle Ansicht für Leitung / Besprechung</small></span>
          </button>
          <button type="button" className="finance-quick__item" onClick={exportOpos} disabled={!canExport}>
            <BadgeEuro size={16} aria-hidden />
            <span className="forq-stack"><strong>OPOS-Liste (CSV)</strong><small>Offene Forderungen & Verbindlichkeiten</small></span>
          </button>
          <button type="button" className="finance-quick__item" onClick={exportDatev} disabled={!canExport}>
            <FileDown size={16} aria-hidden />
            <span className="forq-stack">
              <strong>DATEV-Export</strong>
              <small>{datevBlocked ? 'Gesperrt – Belege prüfen' : 'Buchungsstapel für die Fibu'}</small>
            </span>
          </button>
          <button type="button" className="finance-quick__item" onClick={() => navigate('/finance/auswertungen')}>
            <Send size={16} aria-hidden />
            <span className="forq-stack"><strong>Auswertungen öffnen</strong><small>BWA, Kennzahlen und Berichte</small></span>
          </button>
        </aside>
      </div>
    </WorkspacePage>
  );
}
